import { computed, ref } from "vue";

export type StorefrontTemplate = 'classic' | 'grid' | 'showcase' | 'minimal' | 'dark'
export type StorefrontFont = 'inter' | 'poppins' | 'montserrat' | 'playfair' | 'nunito'
export type StorefrontRadius = 'none' | 'small' | 'medium' | 'large'
export type StorefrontLayout = 'sections' | 'delivery' | 'categories' | 'tabs'
export type StorefrontHeaderStyle = 'compact' | 'cover'
export type StorefrontImageShape = 'square' | 'rounded' | 'circle'
export type StorefrontColumnsMobile = 'auto' | '1' | '2'

export interface StorefrontColors {
    primary: string | null
    secondary: string | null
    background: string | null
    text: string | null
}

export interface StorefrontDisplaySettings {
    layout: StorefrontLayout
    headerStyle: StorefrontHeaderStyle
    coverImageUrl: string | null
    stickyCategoryNav: boolean
    imageShape: StorefrontImageShape
    showAddButton: boolean
    columnsMobile: StorefrontColumnsMobile
    colors: StorefrontColors
}

export interface StorefrontSettings extends StorefrontDisplaySettings {
    font: StorefrontFont
    radius: StorefrontRadius
    showProductImages: boolean
    showProductDescription: boolean
}

interface BaseSection<T extends string, S> {
    id: string
    type: T
    enabled: boolean
    settings: S
}

export type AnnouncementSection = BaseSection<'announcement', { text: string; backgroundColor?: string | null; textColor?: string | null; link?: string | null }>
export type BannersSection = BaseSection<'banners', { style: 'carousel' | 'hero' }>
export type CategoriesSection = BaseSection<'categories', { title?: string | null; style: 'carousel' | 'grid' | 'chips' }>
export type InfoSection = BaseSection<'info', { showMinimumOrder: boolean; showHours: boolean; showWhatsapp: boolean }>
export type FeaturedProductsSection = BaseSection<'featured_products', { title: string; subtitle?: string | null; productIds: number[]; style: 'carousel' | 'grid' }>
export type ImageTextSection = BaseSection<'image_text', { title: string; text?: string | null; imageUrl?: string | null; imagePosition: 'left' | 'right' | 'background'; buttonText?: string | null; buttonLink?: string | null }>
export type MenuSection = BaseSection<'menu', { title?: string | null }>

export type StorefrontSection =
    | AnnouncementSection
    | BannersSection
    | CategoriesSection
    | InfoSection
    | FeaturedProductsSection
    | ImageTextSection
    | MenuSection

export type StorefrontSectionType = StorefrontSection['type']

export interface Storefront {
    template: StorefrontTemplate
    settings: StorefrontSettings
    sections: StorefrontSection[]
}

export type ProductCardVariant = StorefrontTemplate

export const STOREFRONT_TEMPLATES: StorefrontTemplate[] = ['classic', 'grid', 'showcase', 'minimal', 'dark']

export const STOREFRONT_LAYOUTS: StorefrontLayout[] = ['sections', 'delivery', 'categories', 'tabs']
export const STOREFRONT_HEADER_STYLES: StorefrontHeaderStyle[] = ['compact', 'cover']
export const STOREFRONT_IMAGE_SHAPES: StorefrontImageShape[] = ['square', 'rounded', 'circle']
export const STOREFRONT_COLUMNS_MOBILE: StorefrontColumnsMobile[] = ['auto', '1', '2']

/** v2 display settings (same defaults for every template). */
export const DEFAULT_DISPLAY_SETTINGS: StorefrontDisplaySettings = {
    layout: 'sections',
    headerStyle: 'compact',
    coverImageUrl: null,
    stickyCategoryNav: true,
    imageShape: 'rounded',
    showAddButton: true,
    columnsMobile: 'auto',
    colors: { primary: null, secondary: null, background: null, text: null },
}

export type TemplateBaseSettings = Pick<StorefrontSettings, 'font' | 'radius' | 'showProductImages' | 'showProductDescription'>

export const TEMPLATE_DEFAULT_SETTINGS: Record<StorefrontTemplate, TemplateBaseSettings> = {
    classic: { font: 'inter', radius: 'medium', showProductImages: true, showProductDescription: true },
    grid: { font: 'poppins', radius: 'large', showProductImages: true, showProductDescription: false },
    showcase: { font: 'montserrat', radius: 'large', showProductImages: true, showProductDescription: true },
    minimal: { font: 'playfair', radius: 'none', showProductImages: false, showProductDescription: true },
    dark: { font: 'nunito', radius: 'medium', showProductImages: true, showProductDescription: false },
}

export const DEFAULT_SECTIONS: StorefrontSection[] = [
    { id: 'default_categories', type: 'categories', enabled: true, settings: { title: null, style: 'carousel' } },
    { id: 'default_banners', type: 'banners', enabled: true, settings: { style: 'carousel' } },
    { id: 'default_info', type: 'info', enabled: true, settings: { showMinimumOrder: true, showHours: true, showWhatsapp: true } },
    { id: 'default_menu', type: 'menu', enabled: true, settings: { title: null } },
]

export const DEFAULT_STOREFRONT: Storefront = {
    template: 'classic',
    settings: { ...TEMPLATE_DEFAULT_SETTINGS.classic, ...DEFAULT_DISPLAY_SETTINGS, colors: { ...DEFAULT_DISPLAY_SETTINGS.colors } },
    sections: DEFAULT_SECTIONS,
}

export const FONT_FAMILIES: Record<StorefrontFont, { css: string; google: string }> = {
    inter: { css: "'Inter', sans-serif", google: 'Inter:wght@300;400;500;600;700' },
    poppins: { css: "'Poppins', sans-serif", google: 'Poppins:wght@300;400;500;600;700' },
    montserrat: { css: "'Montserrat', sans-serif", google: 'Montserrat:wght@400;500;600;700' },
    playfair: { css: "'Playfair Display', Georgia, serif", google: 'Playfair+Display:wght@400;500;600;700' },
    nunito: { css: "'Nunito', sans-serif", google: 'Nunito:wght@400;500;600;700;800' },
}

export const RADIUS_VALUES: Record<StorefrontRadius, string> = {
    none: '0px',
    small: '6px',
    medium: '12px',
    large: '20px',
}

function cloneDefault(): Storefront {
    return JSON.parse(JSON.stringify(DEFAULT_STOREFRONT))
}

const SECTION_DEFAULT_SETTINGS: Record<StorefrontSectionType, any> = {
    announcement: { text: '', backgroundColor: null, textColor: null, link: null },
    banners: { style: 'carousel' },
    categories: { title: null, style: 'carousel' },
    info: { showMinimumOrder: true, showHours: true, showWhatsapp: true },
    featured_products: { title: '', subtitle: null, productIds: [], style: 'carousel' },
    image_text: { title: '', text: null, imageUrl: null, imagePosition: 'left', buttonText: null, buttonLink: null },
    menu: { title: null },
}

const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i

function normalizeColor(value: any): string | null {
    return typeof value === 'string' && HEX_COLOR.test(value.trim()) ? value.trim() : null
}

function pick<T>(value: any, allowed: T[], fallback: T): T {
    return allowed.includes(value) ? value : fallback
}

/** v2 fields: unknown or invalid values fall back to the defaults. */
function normalizeDisplaySettings(raw: any): StorefrontDisplaySettings {
    const d = DEFAULT_DISPLAY_SETTINGS
    const src = raw && typeof raw === 'object' ? raw : {}
    const colors = src.colors && typeof src.colors === 'object' ? src.colors : {}
    return {
        layout: pick(src.layout, STOREFRONT_LAYOUTS, d.layout),
        headerStyle: pick(src.headerStyle, STOREFRONT_HEADER_STYLES, d.headerStyle),
        coverImageUrl: typeof src.coverImageUrl === 'string' && src.coverImageUrl.trim() ? src.coverImageUrl.trim() : null,
        stickyCategoryNav: typeof src.stickyCategoryNav === 'boolean' ? src.stickyCategoryNav : d.stickyCategoryNav,
        imageShape: pick(src.imageShape, STOREFRONT_IMAGE_SHAPES, d.imageShape),
        showAddButton: typeof src.showAddButton === 'boolean' ? src.showAddButton : d.showAddButton,
        columnsMobile: pick(src.columnsMobile == null ? src.columnsMobile : String(src.columnsMobile), STOREFRONT_COLUMNS_MOBILE, d.columnsMobile),
        colors: {
            primary: normalizeColor(colors.primary),
            secondary: normalizeColor(colors.secondary),
            background: normalizeColor(colors.background),
            text: normalizeColor(colors.text),
        },
    }
}

/**
 * Accepts whatever the API sent (or nothing, for older APIs) and returns a
 * complete, safe storefront. Unknown section types are dropped and the menu
 * section is guaranteed to exist exactly once.
 */
export function normalizeStorefront(raw: any): Storefront {
    if (!raw || typeof raw !== 'object') return cloneDefault()

    const template: StorefrontTemplate = STOREFRONT_TEMPLATES.includes(raw.template) ? raw.template : 'classic'
    const rawSettings = raw.settings && typeof raw.settings === 'object' ? raw.settings : {}
    const base = TEMPLATE_DEFAULT_SETTINGS[template]
    const settings: StorefrontSettings = {
        font: FONT_FAMILIES[rawSettings.font as StorefrontFont] ? rawSettings.font : base.font,
        radius: RADIUS_VALUES[rawSettings.radius as StorefrontRadius] ? rawSettings.radius : base.radius,
        showProductImages: typeof rawSettings.showProductImages === 'boolean' ? rawSettings.showProductImages : base.showProductImages,
        showProductDescription: typeof rawSettings.showProductDescription === 'boolean' ? rawSettings.showProductDescription : base.showProductDescription,
        ...normalizeDisplaySettings(rawSettings),
    }
    if (template === 'minimal') settings.showProductImages = false

    let sections: StorefrontSection[]
    if (!Array.isArray(raw.sections) || !raw.sections.length) {
        sections = cloneDefault().sections
    } else {
        let hasMenu = false
        sections = raw.sections
            .filter((s: any) => s && SECTION_DEFAULT_SETTINGS[s.type as StorefrontSectionType])
            .filter((s: any) => {
                if (s.type !== 'menu') return true
                if (hasMenu) return false
                hasMenu = true
                return true
            })
            .map((s: any, index: number) => ({
                id: String(s.id ?? `sec_${index}`),
                type: s.type,
                enabled: s.type === 'menu' ? true : s.enabled !== false,
                settings: { ...SECTION_DEFAULT_SETTINGS[s.type as StorefrontSectionType], ...(s.settings || {}) },
            }))
        if (!hasMenu) sections.push({ id: 'default_menu', type: 'menu', enabled: true, settings: { title: null } })
    }

    return { template, settings, sections }
}

export const storefront = ref<Storefront>(cloneDefault())

export const storefrontTemplate = computed<StorefrontTemplate>(() => storefront.value.template)

/** Effective flags for product cards (minimal never shows images). */
export const storefrontShowImages = computed<boolean>(() => storefront.value.template !== 'minimal' && storefront.value.settings.showProductImages !== false)
export const storefrontShowDescription = computed<boolean>(() => storefront.value.settings.showProductDescription !== false)
export const storefrontLayout = computed<StorefrontLayout>(() => storefront.value.settings.layout)
export const storefrontImageShape = computed<StorefrontImageShape>(() => storefront.value.settings.imageShape)
export const storefrontShowAddButton = computed<boolean>(() => storefront.value.settings.showAddButton)
export const storefrontColumnsMobile = computed<StorefrontColumnsMobile>(() => storefront.value.settings.columnsMobile)

/** Page background of each template (used to derive colors when only some overrides are set). */
export const TEMPLATE_BACKGROUNDS: Record<StorefrontTemplate, string> = {
    classic: '#ffffff',
    grid: '#ffffff',
    showcase: '#ffffff',
    minimal: '#fbf8f3',
    dark: '#0f0f12',
}

function hexToRgb(hex: string | null | undefined): [number, number, number] | null {
    if (!hex || !HEX_COLOR.test(hex.trim())) return null
    let h = hex.trim().slice(1)
    if (h.length === 3) h = h.split('').map((c) => c + c).join('')
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
}

/** WCAG relative luminance (0 = black, 1 = white). Invalid colors count as white. */
export function colorLuminance(hex: string | null | undefined): number {
    const rgb = hexToRgb(hex)
    if (!rgb) return 1
    const [r, g, b] = rgb.map((v) => {
        const c = v / 255
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
    })
    return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export function isDarkColor(hex: string | null | undefined): boolean {
    return colorLuminance(hex) < 0.18
}

/** WCAG contrast ratio between two colors (1..21). */
export function contrastRatio(a: string, b: string): number {
    const la = colorLuminance(a)
    const lb = colorLuminance(b)
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

/** Default --sf-text on light surfaces (:root) and on dark ones (html.sf-dark), see storefront.css. */
export const LIGHT_SURFACE_TEXT = '#292f31'
export const DARK_SURFACE_TEXT = '#f1f1f4'

/** White or near-black, whichever contrasts more with the given color. */
export function readableTextOn(hex: string | null | undefined): string {
    if (!hexToRgb(hex)) return '#ffffff'
    const l = colorLuminance(hex)
    const withWhite = 1.05 / (l + 0.05)
    const withDark = (l + 0.05) / (colorLuminance('#111111') + 0.05)
    return withDark > withWhite ? '#111111' : '#ffffff'
}

/** Mixes `hex` with `other` (`weight` = share of `other`, 0..1). Returns `hex` when either is invalid. */
export function mixColors(hex: string, other: string, weight: number): string {
    const a = hexToRgb(hex)
    const b = hexToRgb(other)
    if (!a || !b) return hex
    const toHex = (v: number) => Math.round(v).toString(16).padStart(2, '0')
    return '#' + a.map((v, i) => toHex(v * (1 - weight) + b[i] * weight)).join('')
}

const FONT_LINK_ID = 'sf-font-link'

/** Injects the Google Fonts stylesheet only for the fonts actually used. Inter is already bundled by font.scss. */
export function loadStorefrontFonts(fonts: StorefrontFont[]) {
    const needed = Array.from(new Set(fonts)).filter((f) => f !== 'inter' && FONT_FAMILIES[f])
    const existing = document.getElementById(FONT_LINK_ID) as HTMLLinkElement | null
    if (!needed.length) {
        existing?.remove()
        return
    }
    const href = `https://fonts.googleapis.com/css2?${needed.map((f) => `family=${FONT_FAMILIES[f].google}`).join('&')}&display=swap`
    if (existing) {
        if (existing.href !== href) existing.href = href
        return
    }
    const link = document.createElement('link')
    link.id = FONT_LINK_ID
    link.rel = 'stylesheet'
    link.href = href
    document.head.appendChild(link)
}
